// ThemeProvider — controle manual de tema light/dark.
//
// Stack: Context API simples (sem Redux porque é UI puro, sem cross-feature).
// `theme` é o valor escolhido pelo usuário — também é o efetivamente aplicado
// no `<html data-theme>`. Sem modo "system" (decisão de UX: usuário decide).
//
// Persistência: localStorage('chatia:theme'). Inline boot script no
// index.html aplica `data-theme` antes do primeiro paint pra evitar flash.
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';
/** Alias mantido por compat — todo theme atual já é resolved. */
export type ResolvedTheme = Theme;

export interface ThemeContextValue {
  theme: Theme;
  setTheme(next: Theme): void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'chatia:theme';

function readInitialTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'light';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  // Valores antigos ('system'), null ou inválidos → migração silenciosa pra light.
  return 'light';
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Permite override em testes — em produção a inicialização default é suficiente. */
  initialTheme?: Theme;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => initialTheme ?? readInitialTheme());

  // Side-effects: aplica data-theme no <html> + persiste em localStorage.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage cheio / desabilitado — ignora silenciosamente.
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
